import { defineMessages } from 'react-intl.macro';

import { PriceListItemTypesEnum } from '../../openapi/types/PriceListItemTypesEnum';
import { PriceListItemUnitsEnum } from '../../openapi/types/PriceListItemUnitsEnum';

import { PRICE_LIST_ITEM_FORM_TYPE } from '../constants';

export const priceListMessages = defineMessages({
  // LIST
  priceListPageTitle: {
    id: 'priceList.pageTitle',
    defaultMessage: 'Ceník',
  },
  priceListStateWaiting: {
    id: 'priceList.state.waiting',
    defaultMessage: 'Ceník se načítá.',
  },
  priceListStateFail: {
    id: 'priceList.state.fail',
    defaultMessage: 'Ceník se nepovedlo načíst.',
  },
  priceListStateEmpty: {
    id: 'priceList.state.empty',
    defaultMessage: 'Ceník je prázdný.',
  },
  priceListStateFilteredEmpty: {
    id: 'priceList.state.filteredEmpty',
    defaultMessage: 'Zadanému filtru neodpovídá žádná položka ceníku.',
  },
  priceListBtnNew: {
    id: 'priceList.btn.new',
    defaultMessage: 'Vytvořit položku ceníku',
  },
  priceListItemCreated: {
    id: 'priceList.itemCreated',
    defaultMessage: 'Položka ceníku byla vytvořena.',
  },
  priceListItemDeleted: {
    id: 'priceList.itemDeleted',
    defaultMessage: 'Položka ceníku byla smazána.',
  },
  // COLUMNS / FIELDS
  name: {
    id: 'priceListItem.name',
    defaultMessage: 'Název',
  },
  type: {
    id: 'priceListItem.type',
    defaultMessage: 'Typ',
  },
  unit: {
    id: 'priceListItem.unit',
    defaultMessage: 'Jednotka',
  },
  unitPrice: {
    id: 'priceListItem.unitPrice',
    defaultMessage: 'Cena za jednotku',
  },
  vatRate: {
    id: 'priceListItem.vatRate',
    defaultMessage: 'DPH (%)',
  },
  unitPriceWithVat: {
    id: 'priceListItem.unitPriceWithVat',
    defaultMessage: 'Cena za jednotku s DPH',
  },
  created: {
    id: 'priceListItem.created',
    defaultMessage: 'Vytvořeno',
  },
  priceCzk: {
    id: 'priceListItem.priceCzk',
    defaultMessage: '{price} Kč',
  },
  percent: {
    id: 'priceListItem.percent',
    defaultMessage: '{value} %',
  },
  // PRICE BANDS
  bandsTitle: {
    id: 'priceListItem.bands.title',
    defaultMessage: 'Cenová pásma',
  },
  bandsRangeColumn: {
    id: 'priceListItem.bands.rangeColumn',
    defaultMessage: 'Rozloha',
  },
  bandsPriceColumn: {
    id: 'priceListItem.bands.priceColumn',
    defaultMessage: 'Cena bez DPH',
  },
  /** První pásmo, kde `from_ha` je nula. */
  bandsRangeUpTo: {
    id: 'priceListItem.bands.rangeUpTo',
    defaultMessage: 'do {to, number} ha',
  },
  /** `from_ha` exkluzivně, `to_ha` včetně. */
  bandsRangeBetween: {
    id: 'priceListItem.bands.rangeBetween',
    defaultMessage: '{from, number} – {to, number} ha',
  },
  /** Poslední pásmo, kde `to_ha` je null. */
  bandsRangeOver: {
    id: 'priceListItem.bands.rangeOver',
    defaultMessage: 'nad {from, number} ha',
  },
  /** Jediné pásmo od nuly bez horního limitu — cena nezávisí na rozloze. */
  bandsRangeAny: {
    id: 'priceListItem.bands.rangeAny',
    defaultMessage: 'jakákoliv rozloha',
  },
  bandsPriceCzk: {
    id: 'priceListItem.bands.priceCzk',
    defaultMessage: '{price, number} Kč',
  },
  bandsPriceFree: {
    id: 'priceListItem.bands.priceFree',
    defaultMessage: 'zdarma',
  },
  bandsNetNote: {
    id: 'priceListItem.bands.netNote',
    defaultMessage: 'Ceny pásem jsou uvedeny bez DPH. Sazba DPH položky: {vatRate, number} %.',
  },
  bandsEmpty: {
    id: 'priceListItem.bands.empty',
    defaultMessage: 'Pro tento typ zatím nejsou nastavena žádná pásma.',
  },
  /** Pásma převzatá z jiné položky téhož typu, dokud tahle žádná nemá. */
  bandsReferenceNote: {
    id: 'priceListItem.bands.referenceNote',
    defaultMessage: 'Pásma platná pro zvolený typ.',
  },
  /** Shrnutí pásem v přehledu, když se ceny napříč pásmy liší. */
  bandsPriceRange: {
    id: 'priceListItem.bands.priceRange',
    defaultMessage: '{min, number} – {max, number} Kč',
  },
  /** Záloha pro položku bez jednotky nebo s jednotkou, kterou frontend ještě nezná. */
  quantityFallback: {
    id: 'priceListItem.quantityFallback',
    defaultMessage: '{quantity, number} {unit}',
  },
});

export const priceListItemTypeMessages = defineMessages<PriceListItemTypesEnum>({
  [PriceListItemTypesEnum.DATA_VECTOR]: {
    id: 'priceListItem.type.DATA_VECTOR',
    defaultMessage: 'Vektorová data',
  },
  [PriceListItemTypesEnum.DATA_RASTER]: {
    id: 'priceListItem.type.DATA_RASTER',
    defaultMessage: 'Rastrová data',
  },
  [PriceListItemTypesEnum.SERVICES]: {
    id: 'priceListItem.type.SERVICES',
    defaultMessage: 'Služby',
  },
  [PriceListItemTypesEnum.PRINT]: {
    id: 'priceListItem.type.PRINT',
    defaultMessage: 'Tisk',
  },
});

export const priceListItemUnitMessages = defineMessages<PriceListItemUnitsEnum>({
  [PriceListItemUnitsEnum.HOUR]: {
    id: 'priceListItem.unit.HOUR',
    defaultMessage: 'hod',
  },
  [PriceListItemUnitsEnum.PAGE]: {
    id: 'priceListItem.unit.PAGE',
    defaultMessage: 'strana',
  },
  [PriceListItemUnitsEnum.DM2]: {
    id: 'priceListItem.unit.DM2',
    defaultMessage: 'dm²',
  },
});

/**
 * Počet s jednotkou. Jednotka se v češtině řídí počtem (1 strana, 2 strany, 5 stran,
 * 1,5 strany), takže ji nejde slepit z čísla a názvu jednotky v prvním pádě —
 * každá jednotka potřebuje vlastní tvary. Zkratky se neskloňují.
 */
export const priceListItemUnitQuantityMessages = defineMessages<PriceListItemUnitsEnum>({
  [PriceListItemUnitsEnum.HOUR]: {
    id: 'priceListItem.unitQuantity.HOUR',
    defaultMessage: '{quantity, number} hod',
  },
  [PriceListItemUnitsEnum.PAGE]: {
    id: 'priceListItem.unitQuantity.PAGE',
    defaultMessage:
      '{quantity, number} {quantity, plural, one {strana} few {strany} many {strany} other {stran}}',
  },
  [PriceListItemUnitsEnum.DM2]: {
    id: 'priceListItem.unitQuantity.DM2',
    defaultMessage: '{quantity, number} dm²',
  },
});

export const priceListFormTitleMessages = defineMessages<PRICE_LIST_ITEM_FORM_TYPE>({
  [PRICE_LIST_ITEM_FORM_TYPE.NEW]: {
    id: 'priceListForm.title.new',
    defaultMessage: 'Nová položka ceníku',
  },
  [PRICE_LIST_ITEM_FORM_TYPE.EDIT]: {
    id: 'priceListForm.title.edit',
    defaultMessage: 'Detail položky ceníku',
  },
});

export const priceListFormMessages = defineMessages({
  nameErrorMessage: {
    id: 'priceListForm.name.error',
    defaultMessage: 'Zadejte název.',
  },
  typeErrorMessage: {
    id: 'priceListForm.type.error',
    defaultMessage: 'Vyberte typ.',
  },
  unitErrorMessage: {
    id: 'priceListForm.unit.error',
    defaultMessage: 'Vyberte jednotku.',
  },
  unitPriceErrorMessage: {
    id: 'priceListForm.unitPrice.error',
    defaultMessage: 'Zadejte cenu za jednotku (nezáporné číslo).',
  },
  vatRateErrorMessage: {
    id: 'priceListForm.vatRate.error',
    defaultMessage: 'Zadejte sazbu DPH v procentech (0 až 100).',
  },
  unitPriceWithVatValue: {
    id: 'priceListForm.unitPriceWithVat.value',
    defaultMessage: 'Cena za jednotku s DPH: {price} Kč',
  },
  priceListItemAlertDelete: {
    id: 'priceListForm.alert.delete',
    defaultMessage: 'Opravdu chcete smazat tuto položku ceníku?',
  },
});

export const priceListFormBtnMessages = defineMessages({
  create: {
    id: 'priceListForm.btn.create',
    defaultMessage: 'Vytvořit',
  },
  save: {
    id: 'priceListForm.btn.save',
    defaultMessage: 'Uložit',
  },
  cancel: {
    id: 'priceListForm.btn.cancel',
    defaultMessage: 'Zrušit změny',
  },
  undo: {
    id: 'priceListForm.btn.undo',
    defaultMessage: 'Zpět na přehled',
  },
  clear: {
    id: 'priceListForm.btn.clear',
    defaultMessage: 'Vyčistit',
  },
  delete: {
    id: 'priceListForm.btn.delete',
    defaultMessage: 'Smazat',
  },
});

export const priceListBtnMessages = defineMessages({
  filter: {
    id: 'priceList.btn.filter',
    defaultMessage: 'Filtr',
  },
  filterSubmit: {
    id: 'priceList.btn.filterSubmit',
    defaultMessage: 'Filtrovat',
  },
  clear: {
    id: 'priceList.btn.clear',
    defaultMessage: 'Vyčistit',
  },
});

export const priceListFormQueryMessages = defineMessages({
  queryStateWaiting: {
    id: 'priceListForm.query.waiting',
    defaultMessage: 'Probíhá zpracování.',
  },
  loadQueryStateFail: {
    id: 'priceListForm.query.loadFail',
    defaultMessage: 'Položku ceníku se nepodařilo načíst.',
  },
  submitQueryStateSuccess: {
    id: 'priceListForm.query.submitSuccess',
    defaultMessage: 'Položka ceníku byla uložena.',
  },
  deleteQueryStateFail: {
    id: 'priceListForm.query.deleteFail',
    defaultMessage: 'Položku ceníku se nepodařilo smazat.',
  },
  deleteQueryStateFailInUse: {
    id: 'priceListForm.query.deleteFailInUse',
    defaultMessage:
      'Položka je navázaná na výdejní sadu, a proto ji nelze smazat. Nejdřív ji ve všech sadách nahraďte jinou položkou nebo výběr zrušte.',
  },
});

export const priceListFormFailMessages = defineMessages<PRICE_LIST_ITEM_FORM_TYPE>({
  [PRICE_LIST_ITEM_FORM_TYPE.NEW]: {
    id: 'priceListForm.fail.new',
    defaultMessage: 'Položku ceníku se nepodařilo vytvořit.',
  },
  [PRICE_LIST_ITEM_FORM_TYPE.EDIT]: {
    id: 'priceListForm.fail.edit',
    defaultMessage: 'Položku ceníku se nepodařilo uložit.',
  },
});
